import axios from 'axios'
import { useMutation } from '@tanstack/react-query'
import axiosClient from '../../axios'
import { useGet } from '../../query/useGet'

export function useGetZoneComments<T = any>(imageZoneId?: string) {
  const path = `/image-zones/${imageZoneId}/comments`

  const getCommentsForCell = useGet<T>(path)

  const result = getCommentsForCell.data

  return { result, ...getCommentsForCell }
}

export function useAddCommentToZone() {
  const getSendCellComments = useMutation({
    gcTime: 50000,
    mutationFn: ({ imageZoneId, comment }: any) => {
      return axiosClient.post(`/image-zones/${imageZoneId}/comments`, {
        commentText: comment,
      })
    },
  })
  return getSendCellComments
}

export function useDeleteCommentFromZone() {
  const deleteComment = useMutation({
    gcTime: 0,
    // Prefer the nested route; fall back to nested with body/query if not found. Do not hit legacy /comments route anymore.
    mutationFn: async ({
      commentId,
      imageZoneId,
      diagnosticImageId,
      diagnosticId,
    }: {
      commentId: string
      imageZoneId?: string
      diagnosticImageId?: string
      diagnosticId?: string
    }) => {
      if (!imageZoneId) {
        // Fallback to flat route if no zone id was provided
        return axiosClient.delete(`/comments/${commentId}`)
      }

      const endpoints: Array<{ url: string; config?: any }> = [
        { url: `/image-zones/${imageZoneId}/comments/${commentId}` },
        { url: `/image-zones/${imageZoneId}/comments`, config: { data: { commentId } } },
        { url: `/image-zones/${imageZoneId}/comments`, config: { params: { commentId } } },
        { url: `/image-zones/comments/${commentId}` },
        { url: `/image-zones/comments`, config: { params: { commentId, imageZoneId } } },
        { url: `/image-zones/comments`, config: { data: { commentId, imageZoneId } } },
        { url: `/comments/${commentId}` },
        { url: `/comments`, config: { params: { commentId } } },
        { url: `/comments`, config: { data: { commentId } } },
      ]

      if (diagnosticImageId) {
        endpoints.unshift(
          { url: `/diagnostics/diagnostic-images/${diagnosticImageId}/image-zones/${imageZoneId}/comments/${commentId}` },
          {
            url: `/diagnostics/diagnostic-images/${diagnosticImageId}/image-zones/${imageZoneId}/comments`,
            config: { data: { commentId } },
          },
          {
            url: `/diagnostics/diagnostic-images/${diagnosticImageId}/image-zones/${imageZoneId}/comments`,
            config: { params: { commentId } },
          }
        )
      }

      let lastError: unknown
      if (diagnosticId && diagnosticImageId) {
        endpoints.unshift(
          {
            url: `/diagnostics/${diagnosticId}/diagnostic-images/${diagnosticImageId}/image-zones/${imageZoneId}/comments/${commentId}`,
          },
          {
            url: `/diagnostics/${diagnosticId}/diagnostic-images/${diagnosticImageId}/image-zones/${imageZoneId}/comments`,
            config: { data: { commentId } },
          },
          {
            url: `/diagnostics/${diagnosticId}/diagnostic-images/${diagnosticImageId}/image-zones/${imageZoneId}/comments`,
            config: { params: { commentId } },
          }
        )
      } else if (diagnosticImageId) {
        endpoints.unshift(
          { url: `/diagnostics/diagnostic-images/${diagnosticImageId}/image-zones/${imageZoneId}/comments/${commentId}` },
          {
            url: `/diagnostics/diagnostic-images/${diagnosticImageId}/image-zones/${imageZoneId}/comments`,
            config: { data: { commentId } },
          },
          {
            url: `/diagnostics/diagnostic-images/${diagnosticImageId}/image-zones/${imageZoneId}/comments`,
            config: { params: { commentId } },
          }
        )
      }

      for (const { url, config } of endpoints) {
        try {
          return await axiosClient.delete(url, config)
        } catch (error) {
          const is404 = axios.isAxiosError(error) && error.response?.status === 404
          if (!is404) {
            throw error
          }
          if (process.env.NODE_ENV !== 'production') {
            // Help debug differing backend routes during development
            console.warn('[deleteComment] 404, retrying next route', { url, config })
          }
          lastError = error
        }
      }

      throw lastError ?? new Error('Unable to delete comment')
    },
  })
  return deleteComment
}

export function useCreateImageZone() {
  const createImageZoneWithComment = useMutation({
    mutationKey: ['createImageZoneWithComment'],
    mutationFn: (data: {
      diagnosticImageId: string
      coordinates: number[]
      commentText?: string
    }) => {
      return axiosClient.post(`/image-zones`, data)
    },
  })
  return createImageZoneWithComment
}

export function useCleanupAnnotationsWithoutComments() {
  const cleanupAnnotations = useMutation({
    mutationKey: ['cleanupAnnotationsWithoutComments'],
    mutationFn: () => {
      return axiosClient.delete(`/image-zones/cleanup-without-comments`)
    },
  })
  return cleanupAnnotations
}
